import { useState, forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Alert,
  Snackbar,
  Slide,
  CircularProgress,
  Portal,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { Formik } from "formik";
import { createCustomer, updateCustomer } from "../../services/CustomerService";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

// TODO: Validation Schema

const CustomerForm = ({
  open,
  handleClose,
  onCustomerAdded,
  onCustomerEdited,
  editCustomer,
  prefillName,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const editMode = Boolean(editCustomer);

  const [phase, setPhase] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const initialValues = {
    fullName: editCustomer?.fullName ?? prefillName ?? "",
    phoneNumber: editCustomer?.phoneNumber || "",
    email: editCustomer?.email || "",
    storeAddress: editCustomer?.storeAddress || "",
    city: editCustomer?.city || "",
  };

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    return new Promise(async (resolve) => {
      try {
        let response;
        if (editMode) {
          response = await updateCustomer(editCustomer.id, values);
        } else {
          response = await createCustomer(values);
        }

        // Keep loading state visible for 1.5 seconds to match other forms
        setTimeout(() => {
          setPhase(null);
          resetForm();
          handleClose();

          // Refresh the table
          if (editMode) {
            if (onCustomerEdited) onCustomerEdited(response.data);
          } else {
            if (onCustomerAdded) onCustomerAdded(response.data);
          }

          // Show the snackbar slightly after the dialog closes
          setTimeout(() => {
            setSnackbarMessage(
              `Customer ${editMode ? "updated" : "added"} successfully!`,
            );
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            resolve();
          }, 300);
        }, 1500);
      } catch (error) {
        console.error(
          `Failed to ${editMode ? "update" : "create"} customer details: `,
          error,
        );
        setPhase(null);
        setSnackbarMessage(
          `Failed to ${
            editMode ? "update" : "create"
          } customer details. Please try again.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        resolve();
      }
    });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Shared input styling to match the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          {editMode ? "Edit Customer Details" : "Add New Customer"}
        </DialogTitle>

        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            resetForm,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ p: 3 }}>
                <Box
                  display="grid"
                  gap="16px"
                  mt="8px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Full Name"
                    name="fullName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.fullName}
                    error={!!touched.fullName && !!errors.fullName}
                    helperText={touched.fullName && errors.fullName}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Phone Number"
                    name="phoneNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phoneNumber}
                    error={!!touched.phoneNumber && !!errors.phoneNumber}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Store Address"
                    name="storeAddress"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.storeAddress}
                    error={!!touched.storeAddress && !!errors.storeAddress}
                    helperText={touched.storeAddress && errors.storeAddress}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: "span 4",
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Email"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="City"
                    name="city"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.city}
                    error={!!touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />
                </Box>
              </DialogContent>

              <Divider
                sx={{
                  mb: 0,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.12)",
                }}
              />

              <DialogActions
                sx={{
                  px: 3,
                  pb: 3,
                  pt: { xs: 2, sm: 3 },
                  gap: { xs: 2, sm: 2 },
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  "& > :not(style) ~ :not(style)": {
                    ml: "0 !important",
                  },
                }}
              >
                <Button
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  disabled={phase === "loading"}
                  variant="outlined"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    color: colors.gray[100],
                    borderColor: colors.gray[500],
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: colors.gray[300],
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={phase === "loading"}
                  variant="contained"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: colors.blueAccent[600],
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(79, 70, 229, 0.2)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[500],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 6px 14px rgba(0,0,0,0.4)"
                          : "0 6px 16px rgba(79, 70, 229, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.12)",
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {phase === "loading" ? (
                    <>
                      {editMode ? "Updating" : "Creating"}&nbsp;
                      <CircularProgress
                        size={18}
                        sx={{ color: "inherit", ml: "8px" }}
                      />
                    </>
                  ) : editMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default CustomerForm;
